import { notifications } from "@mantine/notifications";
import { IconCheck, IconX } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import {
  useGetPaymentConfigQuery,
  useSavePaymentConfigMutation,
  useUpdatePaymentConfigMutation,
} from "Services/Admin/AdminPaymentApiSlice";
import CircleLoader from "Templates/Template1/Components/CircleLoader/CircleLoader";
import { Eye, EyeOff } from "lucide-react";

interface PaymentFormData {
  gatewayProvider: string;
  email: string;
  accessTokenId: string;
  clientId: string;
  secretId: string;
  environment: string;
  isActive: string;
}

const AdminPaymentTab = () => {
  const { data: paymentConfig, isLoading, error, isError } = useGetPaymentConfigQuery();
  const [savePaymentConfig] = useSavePaymentConfigMutation();
  const [updatePaymentConfig] = useUpdatePaymentConfigMutation();
  const [showSecretId, setShowSecretId] = useState(false);

  useEffect(() => {
  }, [paymentConfig, isLoading, isError, error]);

  const [formData, setFormData] = useState<PaymentFormData>({
    gatewayProvider: "Stripe",
    email: "",
    accessTokenId: "",
    clientId: "",
    secretId: "",
    environment: "0",
    isActive: "1",
  });

  useEffect(() => {
    if (paymentConfig?.results) {
      const config = paymentConfig.results;
      setFormData({
        gatewayProvider: config.gatewayProvider || "Stripe",
        email: config.email || "",
        accessTokenId: config.accessTokenId || "",
        clientId: config.clientId || "",
        secretId: config.secretId || "",
        environment: config.environment?.toString() || "0",
        isActive: config.isActive?.toString() || "1",
      });
    }
  }, [paymentConfig]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? (checked ? "1" : "0") : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (paymentConfig) {
        const updatePayload = {
          ...formData,
          id: paymentConfig.results.id,
          isActive: formData.isActive,
        };

        const updateRes = await updatePaymentConfig(updatePayload).unwrap();

        notifications.show({
          message: updateRes.message,
          color: "green",
          icon: <IconCheck size={18} />,
        });
      } else {
        const createPayload = {
          ...formData,
          isActive: formData.isActive,
        };

        const createRes = await savePaymentConfig(createPayload).unwrap();

        notifications.show({
          message: createRes.message,
          color: "green",
          icon: <IconCheck size={18} />,
        });
      }
    } catch (error: any) {
      console.error("Failed to save payment config:", error);
      notifications.show({
        message: (error as any)?.data?.message || "An error occurred",
        color: "red",
        icon: <IconX size={18} />,
      });
    }
  };

  if (isLoading) return <div className="w-full h-screen justify-center items-center"><CircleLoader /></div>;

  return (
    <div className="font-body tracking-wider flex justify-center items-center">
      <form onSubmit={handleSubmit} className="p-6 rounded w-full max-w-3xl">
        <h2 className="text-sm font-medium mb-6 text-center">
          {paymentConfig ? "Update Payment Gateway" : "Create Payment Gateway"}
        </h2>

        <div className="mb-4 flex flex-col lg:flex-row lg:items-center">
          <label className="w-full lg:w-48 text-xs font-medium text-gray-700 mb-2 lg:mb-0">Gateway Provider:</label>
          <select
            name="gatewayProvider"
            value={formData.gatewayProvider}
            onChange={handleChange}
            className="border p-2 w-full max-w-md rounded text-xs font-medium text-gray-700"
          >
            <option value="stripe">Stripe</option>
            <option value="razorpay">Razorpay</option>
          </select>
        </div>

        <div className="mb-4 flex flex-col lg:flex-row lg:items-center">
          <label className="w-full lg:w-48 text-xs font-medium text-gray-700 mb-2 lg:mb-0">*Email:</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="border p-2 w-full max-w-md rounded text-xs font-medium text-gray-700"
            required
          />
        </div>

        {
          formData.gatewayProvider !== "razorpay" &&
            <div className="mb-4 flex flex-col lg:flex-row lg:items-center">
              <label className="w-full lg:w-48 text-xs font-medium text-gray-700 mb-2 lg:mb-0">*Access Token Id:</label>
              <input
                type="text"
                name="accessTokenId"
                value={formData.accessTokenId}
                onChange={handleChange}
                className="border p-2 w-full max-w-md rounded text-xs font-medium text-gray-700"
                required
              />
            </div>
        }
        

        <div className="mb-4 flex flex-col lg:flex-row lg:items-center">
          <label className="w-full lg:w-48 text-xs font-medium text-gray-700 mb-2 lg:mb-0">*Client Id:</label>
          <input
            type="text"
            name="clientId"
            value={formData.clientId}
            onChange={handleChange}
            className="border p-2 w-full max-w-md rounded text-xs font-medium text-gray-700"
            required
          />
        </div>

        <div className="mb-4 flex flex-col lg:flex-row lg:items-center">
          <label className="w-full lg:w-48 text-xs font-medium text-gray-700 mb-2 lg:mb-0">*Secret Id:</label>
          <div className="relative w-full max-w-md">
            <input
              type={showSecretId ? "text" : "password"}
              name="secretId"
              value={formData.secretId}
              onChange={handleChange}
              className="border p-2 w-full rounded text-xs font-medium text-gray-700"
              required
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              onClick={() => setShowSecretId((prev) => !prev)}
            >
              {showSecretId ? (
                <EyeOff size={16} strokeWidth={2.5} />
              ) : (
                <Eye size={16} strokeWidth={2.5} />
              )}
            </button>
          </div>
        </div>

        <div className="mb-4 flex flex-col lg:flex-row lg:items-center">
          <label className="w-full lg:w-48 text-xs font-medium text-gray-700 mb-2 lg:mb-0">*Environment:</label>
          <div className="flex gap-6">
            <label className="flex items-center text-xs font-medium text-gray-700 cursor-pointer">
              <input
                type="radio"
                name="environment"
                value="0"
                checked={formData.environment === "0"}
                onChange={handleChange}
                className="mr-2"
              />
              Sandbox
            </label>
            <label className="flex items-center text-xs font-medium text-gray-700 cursor-pointer">
              <input
                type="radio"
                name="environment"
                value="1"
                checked={formData.environment === "1"}
                onChange={handleChange}
                className="mr-2"
              />
              Live
            </label>
          </div>
        </div>

        <div className="flex">
          <button
            type="submit"
            className="text-xs font-medium bg-blue-600 ml-auto text-white px-6 py-2 rounded hover:bg-blue-700"
          >
            {paymentConfig ? "Update Configuration" : "Save Configuration"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminPaymentTab;